import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import { useAuth } from '../../../context/AuthContext';
import { getAccommodationTimerDuration, isAccommodationTimerUntimed } from '../../../policies/studentAccommodationPolicy';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { progressQueue } from '../../../services/ProgressQueueService';
import { PRACTICE_BADGE_CONFIG, PRACTICE_PHASES } from '../shared';

interface FluencyScreenProps {
  settings: KPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const FluencyScreen: React.FC<FluencyScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const timerDurationMs = getAccommodationTimerDuration(6000, user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const isUntimed = isAccommodationTimerUntimed(user?.iepAccommodationsEnabled, user?.iepTimerMode);
  const [currentFact, setCurrentFact] = useState<{ factId: string; digit: number } | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());
  const factScoresRef = useRef(factScores);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);

  // Hidden timer state
  const [timeRemaining, setTimeRemaining] = useState(timerDurationMs);
  const [timerActive, setTimerActive] = useState(false);

  // Timer tracking for attempt data
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Digit to fact mapping (digits 0-20 map to facts 1602-1622)
  const digitToFact = useCallback((digit: number): string => {
    return `FACT${1602 + digit}`; // 1602-1622 for digits 0-20
  }, []);

  // Reset timer helper function
  const resetTimer = useCallback((shouldStartTimer: boolean = false) => {
    setTimeRemaining(timerDurationMs);
    setTimerActive(shouldStartTimer && !isUntimed);
    setQuestionStartTime(Date.now()); // Reset timer for new question
  }, [timerDurationMs, isUntimed]);

  // Generate answer choices (target digit + 3 other options)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    // Add 3 other numbers between 0-20, avoiding target
    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 21);
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    // Shuffle the choices
    return choices.sort(() => Math.random() - 0.5);
  }, []);


  const currentDigit = currentFact?.digit;

  // Keep factScoresRef in sync with factScores state
  useEffect(() => {
    factScoresRef.current = factScores;
  }, [factScores]);

  // Initialize first fact when component loads
  useEffect(() => {
    if (!currentFact && factsToPractice.length > 0) {
      setCurrentFact(factsToPractice[0]);
    }
  }, [currentFact, factsToPractice]);

  // Initialize answer choices when digit changes
  useEffect(() => {
    if (currentDigit !== undefined) {
      setAnswerChoices(generateAnswerChoices(currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      resetTimer(false);
    }
  }, [currentDigit, generateAnswerChoices, resetTimer]);

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && currentDigit !== undefined) {
      const audioFile = QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber];

      playTTSWithCallback(audioFile, () => {
        setTtsHasPlayed(true);
        resetTimer(true);
      }).catch(() => {
        setTtsHasPlayed(true);
        resetTimer(true);
      });
    } else if (!settings.speechEnabled) {
      setTtsHasPlayed(true);
      resetTimer(true);
    }
  }, [currentDigit, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed, resetTimer]);

  // Cleanup all timeouts on unmount
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  // Hidden 6-second countdown timer
  useEffect(() => {
    if (isUntimed || !timerActive || hasAnswered || timeRemaining <= 0) {
      if (timeRemaining <= 0 && !hasAnswered && currentFact) {
        // Time expired - treat as incorrect answer
        setHasAnswered(true);
        setTimerActive(false);

        // Calculate response time (full timer duration)
        const responseTime = Date.now() - questionStartTime;

        // Report timeout attempt data to progress queue
        if (user?.userId && currentFact) {
          try {
            progressQueue.enqueueProgressUpdate(
              user.userId,
              {
                facts: {
                  [currentFact.factId]: {
                    attempts: 1,
                    correct: 0, // Timeout is incorrect
                    timeSpent: responseTime
                  }
                }
              }
            );
          } catch (error) {
            console.error('Error reporting K number naming fluency timeout:', error);
          }
        }

        // Update fact score: -1 for incorrect
        setFactScores(prev => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore - 1;
          newScores.set(currentFact.factId, newScore);

          // If score goes below 1, decrement counter (was previously counted but now incorrect)
          if (currentScore >= 1 && newScore < 1) {
            setCompletedCount(prevCount => prevCount - 1);
          }

          return newScores;
        });

        // Reset to allow another attempt after feedback
        const timeoutId = setTimeout(() => {
          setHasAnswered(false);
          resetTimer(true);
        }, 1500);
        timeoutsRef.current.push(timeoutId);
      }
      return;
    }

    const timer = setTimeout(() => {
      setTimeRemaining(prev => prev - 100);
    }, 100);

    return () => clearTimeout(timer);
  }, [isUntimed, timeRemaining, timerActive, hasAnswered, currentFact, user, currentDigit, digitToFact, questionStartTime]);

  const advanceToNext = useCallback(() => {
    // Check if all facts have score >= 1 (which means 2 correct answers due to async state)
    const latestScores = factScoresRef.current;
    const eligibleFacts = factsToPractice.filter(fact => {
      const score = latestScores.get(fact.factId) || 0;
      return score < 1; // Changed from < 2 to < 1 due to async state updates
    });

    if (eligibleFacts.length === 0) {
      // All facts completed
      onComplete();
      return;
    }

    // Select a new random fact from eligible facts (score < 2)
    const randomIndex = Math.floor(Math.random() * eligibleFacts.length);
    setCurrentFact(eligibleFacts[randomIndex]);

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
  }, [factsToPractice, factScores, onComplete]);

  const handleAnswerSubmission = useCallback((isCorrect: boolean) => {
    if (!currentFact) return;

    setHasAnswered(true);
    setTimerActive(false);

    // Calculate response time
    const responseTime = Date.now() - questionStartTime;

    // Report attempt data to progress queue
    if (user?.userId && currentFact) {
      try {
        progressQueue.enqueueProgressUpdate(
          user.userId,
          {
            facts: {
              [currentFact.factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: responseTime
              }
            }
          }
        );
      } catch (error) {
        console.error('Error reporting K number naming fluency attempt:', error);
      }
    }

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      // Update fact score: +1 for correct
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFact.factId, newScore);

        // Only increment counter if going from 0 to 1 (first genuine correct)
        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);
        }

        // Update backend fact status to fluency3Practice when score reaches 1
        // (which represents 2 correct answers due to async state timing)
        if (newScore === 1 && user?.userId) {
          const timeoutId = setTimeout(async () => {
            try {
              const progressUpdate: SetUserProgressRequest = {
                facts: {
                  [currentFact.factId]: {
                    status: 'fluency3Practice'
                  }
                }
              };

              await setUserProgress(user.userId, progressUpdate);
            } catch (error) {
              console.error('Failed to update fact status to fluency3Practice:', error);
            }
          }, 0);
          timeoutsRef.current.push(timeoutId);
        }

        return newScores;
      });

      // Play chime and advance after a delay
      playChime().then(() => {
        const timeoutId = setTimeout(() => {
          advanceToNext();
        }, 500);
        timeoutsRef.current.push(timeoutId);
      });
    } else {
      // Incorrect answer

      // Update fact score: -1 for incorrect
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFact.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFact.factId, newScore);

        // If score goes below 1, decrement counter (was previously counted but now incorrect)
        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      // Reset to allow another attempt after feedback
      const timeoutId = setTimeout(() => {
        setHasAnswered(false);
        resetTimer(true);
      }, 1500);
      timeoutsRef.current.push(timeoutId);
    }
  }, [currentFact, playChime, advanceToNext, user, questionStartTime, currentDigit, digitToFact]);

  const handleAnswerClick = useCallback((selectedDigit: number) => {
    // Don't allow clicks while TTS is playing or already answered
    if (isTTSPlaying || hasAnswered || !currentFact) return;

    const isCorrect = selectedDigit === currentDigit;
    handleAnswerSubmission(isCorrect);
  }, [currentDigit, hasAnswered, isTTSPlaying, currentFact, handleAnswerSubmission]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row - Back Button, Practice Indicator and Progress */}
      <div className="flex justify-between items-center p-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        {/* Practice Indicator */}
        <div className={`${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].bgColor} ${PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].textColor} px-6 py-3 rounded-full font-bold text-lg shadow-lg`}>
          {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].emoji} {PRACTICE_BADGE_CONFIG[PRACTICE_PHASES.FLUENCY].label}
        </div>

        {/* Timer Display - Shows overall progress through numbers */}
        <div className="relative w-16 h-16 opacity-40">
          {/* Background circle */}
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#d1d5db"
              strokeWidth="1.5"
            />
            {/* Progress circle - shows progress through all facts */}
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray="87.96"
              strokeDashoffset={87.96 * (1 - (completedCount / factsToPractice.length))}
              className="transition-all duration-300 ease-out"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Area - Centered Answer Choices */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="grid grid-cols-2 gap-8 lg:gap-12 w-full max-w-md lg:max-w-lg">
          {answerChoices.map((choice, index) => (
            <motion.button
              key={choice}
              initial={{ opacity: 0, y: 30, rotate: -5 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              whileHover={!hasAnswered && !isTTSPlaying ? { scale: 1.08, rotate: 1 } : {}}
              whileTap={!hasAnswered && !isTTSPlaying ? { scale: 0.92 } : {}}
              onClick={() => handleAnswerClick(choice)}
              disabled={hasAnswered || isTTSPlaying}
              className={`
                h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                ${hasAnswered && choice === currentDigit
                  ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                  : hasAnswered || isTTSPlaying
                  ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                }
                ${hasAnswered || isTTSPlaying ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl transform-gpu'}
              `}
              style={NUMERAL_FONT_STYLE}
            >
{choice}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FluencyScreen;
